<template>
    <transition name="slide-up">
      <div v-if="visible">
        <div class="modal-overlay" @click="$emit('close')"></div>
        <div class="modal-notification" aria-live="assertive" aria-atomic="true" role="alert">
          <div class="modal-header">
            <h3>
              <span class="alarm-icon">⏰</span> {{$t('Alarm / Reminder')}}
            </h3>
            <button class="close-button" @click="$emit('close')">&times;</button>
          </div>
          <div class="modal-body">
            <p class="current-time" aria-live="polite"><strong>{{ $t('Current time:')}}</strong> {{ currentTime }}</p>
            <p class="message">{{ message }}</p>
            <button class="action-button" @click="stopAlarmSound(); $emit('mark-as-read')">
                {{ $t('Pause')}}
            </button>
          </div>
        </div>
      </div>
    </transition>
</template>
  
<script lang="ts">
import { defineComponent } from "vue";

export default defineComponent({
    name: "NotificationModal",
    props: {
        visible: {
            type: Boolean,
            required: true,
        },
        message: {
            type: String,
            default: ""
        }
    },
    data() {
        return {
            currentTime: "",
            isPaused: false,
            timer: null as ReturnType<typeof setInterval> | null,
            alarmAudio: null as HTMLAudioElement | null
        };
    },
    methods: {
        updateTime() {
            const now = new Date();
            this.currentTime = now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
        },
        togglePause() {
            this.isPaused = !this.isPaused;
            this.$emit("pause-toggled", this.isPaused);
        },
        playAlarmSound() {
            try {
                if (this.alarmAudio) {
                    this.alarmAudio.currentTime = 0;
                    this.alarmAudio.play().catch(error => {
                        console.warn('Could not play alarm sound:', error);
                    });
                }
            } catch (error) {
                console.warn('Error playing alarm sound:', error);
            }
        },
        stopAlarmSound() {
            try {
                if (this.alarmAudio) {
                    this.alarmAudio.pause();
                    this.alarmAudio.currentTime = 0;
                }
            } catch (error) {
                console.warn('Error stopping alarm sound:', error);
            }
        }
    },
    watch: {
        visible(val: boolean) {
            if (val) {
                this.updateTime();
                this.timer = setInterval(() => this.updateTime(), 1000);
                this.playAlarmSound();
            } else {
                if (this.timer) {
                    clearInterval(this.timer);
                    this.timer = null;
                }
                this.stopAlarmSound();
            }
        }
    },
    mounted(){
        this.updateTime();
        // Inicializar el audio
        this.alarmAudio = new Audio();
        this.alarmAudio.src = '/audio/alarm.mp3';
        this.alarmAudio.preload = 'auto';
        this.alarmAudio.loop = true;
    },
    unmounted() {
        if (this.timer) {
            clearInterval(this.timer);
        }
        // Limpiar el audio
        this.stopAlarmSound();
        if (this.alarmAudio) {
            this.alarmAudio = null;
        }
    }
});
</script>
  
<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.4);
  z-index: 9998;
}

.dark-theme .modal-notification{
  background-color: var(--primary-color);
}
.dark-theme .action-button{
  background-color: #8e1313;
  border: 1px solid;
}
.modal-notification {
  position: fixed;
  top: 50px;
  right: 0;
  left: 0;
  margin: 0 auto;
  width: 500px;
  background-color: #fff;
  border-left: 5px solid var(--primary-color);
  border-radius: 8px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
  padding: 16px;
  z-index: 9999;
}

.current-time {
    margin-top: 0;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: bold;
  color: var(--primary-color);
}

.modal-body {
  margin-top: 10px;
  font-size: 14px;
  color: #333;
}

.close-button {
  background: none;
  border: none;
  font-size: 20px;
  color: #999;
  cursor: pointer;
}

.close-button:hover {
  color: #000;
}

.action-button {
  margin-top: 12px;
  padding: 6px 12px;
  background-color: #e3342f;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.action-button:hover {
  background-color: #c81e1e;
}

/* Animaciones */
.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.3s ease;
}

.slide-up-enter,
.slide-up-leave-to {
  transform: translateY(100%);
  opacity: 0;
}

.slide-up-enter-to,
.slide-up-leave {
  transform: translateY(0);
  opacity: 1;
}

@keyframes alarm-shake {
  0% { transform: rotate(0deg); }
  25% { transform: rotate(15deg); }
  50% { transform: rotate(-15deg); }
  75% { transform: rotate(15deg); }
  100% { transform: rotate(0deg); }
}

.alarm-icon {
  display: inline-block;
  animation: alarm-shake 0.6s infinite;
  transform-origin: center;
}

@media (max-width: 639px) {
    .modal-notification{
        width: 90%;
        padding: 13px;
    }
    .modal-notification .modal-body{
        padding: 10px;
    }
    .modal-body p{
        font-size: 13px;
    }
}
</style>
